import { type JSX } from 'react';
import { generatePath } from 'react-router';

import { TprmQuestionnaireApi } from 'Api/TPRM/TprmQuestionnaireApi';
import { PageDataLoader } from 'Components/PageDataLoader/PageDataLoader';
import { PageLayoutDefault } from 'Components/PageLayout/PageLayoutDefault';
import { ColumnConfig, ColumnType } from 'Components/Table/Columns';
import { Table } from 'Components/Table/Table';
import { TPRM_PUBLIC_QUESTIONNAIRE, TPRM_QUESTIONNAIRE_LIST } from 'Config/PathTemplates';
import { BasicAuthHandler } from 'Helpers/Auth/BasicAuth/BasicAuthHandler';
import { isQuestionAnswered } from 'Models/DueDiligence';
import { PublicQuestionnaire } from 'Models/TPRM';

export interface ThirdPartyQuestionnaireListingLoaderProps {
    authHandler: BasicAuthHandler;
    questionnaireApi: TprmQuestionnaireApi;
}

export const ThirdPartyQuestionnaireListingLoader = (props: ThirdPartyQuestionnaireListingLoaderProps) => {
    return <PageDataLoader path={TPRM_QUESTIONNAIRE_LIST} fetchData={(urlParams) => Promise.all([props.questionnaireApi.getClientAndThirdPartyDetails(), props.questionnaireApi.getAllQuestionnairesForService(urlParams.third_party_id, urlParams.service_id)])} renderForm={(result, params) => <ThirdPartyQuestionnaireListing authHandler={props.authHandler} clientId={params.client_id} clientName={result[0].data.client_name} questionnaires={result[1].data} serviceName={result[0].data.service_name} thirdPartyName={result[0].data.third_party_name} />} />;
};

export interface ThirdPartyQuestionnaireListingProps {
    authHandler: BasicAuthHandler;
    clientId: string;
    clientName: string;
    questionnaires: PublicQuestionnaire[];
    serviceName: string;
    thirdPartyName: string;
}

/** Renders a table of all questionnaires a third party has been requested to complete for a due diligence worfklow. This is the landing page for logged-in third-party users. */
const ThirdPartyQuestionnaireListing = (props: ThirdPartyQuestionnaireListingProps): JSX.Element => {
    const columns: ColumnConfig<PublicQuestionnaire>[] = [
        {
            type: ColumnType.STRING,
            name: 'Name',
            getString: (questionnaire) => questionnaire.name,
            getOnClickTextAction: (questionnaire) => ({
                goToLocation: generatePath(TPRM_PUBLIC_QUESTIONNAIRE, {
                    client_id: props.clientId,
                    third_party_id: questionnaire.third_party_id,
                    service_id: questionnaire.service_id,
                    assessment_id: questionnaire.id,
                }),
            }),
        },
        {
            type: ColumnType.PRIMARY_AND_SECONDARY_STRING,
            name: 'Status',
            getPrimaryString: (questionnaire) => (questionnaire.submitted_timestamp ? 'Submitted' : 'In progress'),
            getSecondaryString: (questionnaire) => {
                const questions = questionnaire.control_frameworks.flatMap((framework) => framework.control_groups.flatMap((group) => group.controls.flatMap((control) => control.questions)));
                const answeredCount = questions.filter(isQuestionAnswered).length;
                return `${answeredCount}/${questions.length} questions answered`;
            },
        },
        {
            type: ColumnType.DATE,
            name: 'Due Date',
            getDateString: (questionnaire) => questionnaire.due_date,
        },
    ];

    return (
        <PageLayoutDefault
            headerTitle="Questionnaires"
            headerDescription={`${props.clientName} is performing a due diligence assessment of ${props.thirdPartyName} - ${props.serviceName} and has requested that ${props.thirdPartyName} completes the following questionnaires. Please complete each questionnaire before its due date.`}
            body={[
                {
                    content: <Table<PublicQuestionnaire> columns={columns} defaultSortColumn="Name" filters={[]} getUniqueIdForRow={(questionnaire) => questionnaire.id} rowsData={props.questionnaires} />,
                },
            ]}
        />
    );
};
